/**
 * The per-course look-and-feel document the roadmap maps read and the
 * look-and-feel editor writes.
 *
 * Stored at `roadmap/{courseId}/descriptions/lookAndFeel`, beside the module
 * descriptions, because that collection is already readable by everyone and
 * writable by course admins. Every field has a default so a missing or
 * partial document renders the maps exactly as they looked before the
 * editor existed.
 */
import {z} from 'zod';

/** A 6-digit hex colour. */
const HexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/);

/**
 * One widget placed on a land. Positions are fractions of the canvas, which
 * has the same logical size on every screen.
 */
export const PlacementSchema = z.object({
  /** Stable id, unique within the course. */
  id: z.string(),
  /** A widget id from the catalogue in `widgets.tsx`; unknown ids are skipped. */
  widget: z.string(),
  /** Horizontal position as a fraction of the canvas width, in [0, 1]. */
  x: z.number().min(0).max(1),
  /**
   * Vertical position as a fraction of the land's content height, from just
   * under the header (0) to the land's horizon (1).
   */
  y: z.number().min(0).max(1),
  /** Uniform scale. */
  scale: z.number().min(0.4).max(2.5).default(1),
  /** Mirror the widget left-to-right. */
  flip: z.boolean().default(false),
  /** Widget-specific option, e.g. a mascot's expression. */
  variant: z.string().optional(),
  /** Free text for widgets that show one. */
  text: z.string().max(80).optional(),
});

/** A placed widget. */
export type Placement = z.infer<typeof PlacementSchema>;

/**
 * Where a coin sits instead of its place on the road. `x` is a fraction of
 * the canvas width; `dy` is a vertical nudge in pixels from the coin's slot.
 * The road bends to pass through the moved coin.
 */
export const StonePositionSchema = z.object({
  x: z.number().min(0).max(1),
  dy: z.number().min(-80).max(80).default(0),
});

/** A coin's position override. */
export type StonePosition = z.infer<typeof StonePositionSchema>;

/** Per-week overrides. */
export const WeekLookSchema = z.object({
  /** The land's title, replacing the module title. */
  title: z.string().max(60).optional(),
  /** The land's accent (banner, coins, ink), replacing the default cycle. */
  accent: HexColorSchema.optional(),
  /** The land's ground tint. Derived from the accent when omitted. */
  ground: HexColorSchema.optional(),
  /** The land's road colour. Derived from ground and accent when omitted. */
  road: HexColorSchema.optional(),
  /** Widgets placed on this land. */
  decorations: z.array(PlacementSchema).default([]),
  /** Coins moved off the road, by item id. */
  stones: z.record(z.string(), StonePositionSchema).default({}),
  /**
   * Ids of automatically placed props and mascots this land no longer
   * draws, because they were adopted into `decorations` or removed.
   */
  hiddenAuto: z.array(z.string()).default([]),
});

/** Per-week overrides. */
export type WeekLook = z.infer<typeof WeekLookSchema>;

/** The whole document. */
export const LookAndFeelSchema = z.object({
  /** Show the plush mascots in the margins and beside the current coin. */
  showMascots: z.boolean().default(true),
  /** Show the automatically placed scenery in the margins. */
  showAutoScenery: z.boolean().default(true),
  /** End the map with the closing card. */
  showFinale: z.boolean().default(true),
  finaleTitle: z.string().max(60).default("That's all for now."),
  finaleSubtitle: z
    .string()
    .max(120)
    .default('New weeks appear here as the course unfolds.'),
  /** Label over the shelf of optional items. */
  goingFurtherLabel: z.string().max(40).default('Going further · optional'),
  /** Overrides by week id (the roadmap module id). */
  weeks: z.record(z.string(), WeekLookSchema).default({}),
});

/** The course's look and feel. */
export type LookAndFeel = z.infer<typeof LookAndFeelSchema>;

/** What the maps show when the course has no document. */
export const DEFAULT_LOOK_AND_FEEL: LookAndFeel = LookAndFeelSchema.parse({});

/**
 * Parses a Firestore document leniently: unknown or malformed fields fall
 * back to their defaults rather than failing the whole document.
 *
 * @param data - The raw document data, or undefined when it does not exist.
 * @return A complete look and feel.
 * @throws Never.
 */
export function parseLookAndFeel(data: unknown): LookAndFeel {
  const whole = LookAndFeelSchema.safeParse(data ?? {});
  if (whole.success) {
    return whole.data;
  }
  // Salvage field by field so one bad week does not discard the rest.
  const record =
    typeof data === 'object' && data !== null
      ? (data as Record<string, unknown>)
      : {};
  const weeks: Record<string, WeekLook> = {};
  const rawWeeks = record.weeks;
  if (typeof rawWeeks === 'object' && rawWeeks !== null) {
    for (const [id, week] of Object.entries(rawWeeks)) {
      const parsed = WeekLookSchema.safeParse(week);
      if (parsed.success) {
        weeks[id] = parsed.data;
      }
    }
  }
  const rest = LookAndFeelSchema.omit({weeks: true}).safeParse(record);
  return {
    ...(rest.success ? rest.data : DEFAULT_LOOK_AND_FEEL),
    weeks,
  };
}

/**
 * The look for one week, with defaults filled in.
 *
 * @param lookAndFeel - The course's look and feel.
 * @param weekId - The week's id.
 * @return The week's overrides, empty when none are stored.
 */
export function weekLook(lookAndFeel: LookAndFeel, weekId: string): WeekLook {
  // Defaults first, so a week written before a field existed still has it.
  return {
    decorations: [],
    stones: {},
    hiddenAuto: [],
    ...lookAndFeel.weeks[weekId],
  };
}
